import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { getActiveAnnouncement } from '../api/announcements';

const AnnouncementContext = createContext();

export const useAnnouncement = () => {
  const context = useContext(AnnouncementContext);
  if (!context) {
    throw new Error('useAnnouncement must be used within an AnnouncementProvider');
  }
  return context;
};

export const AnnouncementProvider = ({ children }) => {
  const [announcement, setAnnouncement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Fetch active announcement on mount
  useEffect(() => {
    let cancelled = false;
    const fetchAnnouncement = async () => {
      setIsLoading(true);
      try {
        const data = await getActiveAnnouncement();
        if (!cancelled) {
          setAnnouncement(data);
          // Check if this announcement was previously dismissed
          if (data?.id) {
            const dismissedId = sessionStorage.getItem('dismissed_announcement_id');
            if (dismissedId === String(data.id)) {
              setDismissed(true);
            }
          }
        }
      } catch (error) {
        console.error('[AnnouncementContext] Failed to fetch announcement:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    fetchAnnouncement();
    return () => { cancelled = true; };
  }, []);

  // Dismiss the current announcement for this session
  const dismissAnnouncement = useCallback(() => {
    if (announcement?.id) {
      sessionStorage.setItem('dismissed_announcement_id', String(announcement.id));
    }
    setDismissed(true);
  }, [announcement?.id]);

  // The announcement to show (null if dismissed or not loaded)
  const activeAnnouncement = useMemo(() => {
    if (dismissed || isLoading || !announcement) {
      return null;
    }
    return announcement;
  }, [announcement, dismissed, isLoading]);

  // Use a fixed banner height to avoid layout shifts
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--announcement-banner-height',
      activeAnnouncement ? '36px' : '0px'
    );
    return () => {
      document.documentElement.style.removeProperty('--announcement-banner-height');
    };
  }, [activeAnnouncement]);

  const value = useMemo(() => ({
    announcement: activeAnnouncement,
    isLoading,
    dismissAnnouncement,
  }), [activeAnnouncement, isLoading, dismissAnnouncement]);

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
};
